import { useState, useEffect } from 'react';
import { supabase, Database } from '../lib/supabase';
import { useAuth } from './useAuth';
import { SubscriptionService } from '../lib/subscription';
import { seedActivitiesForChild } from '../lib/activity-seeding';

type Child = Database['public']['Tables']['children']['Row'];
type ChildInsert = Database['public']['Tables']['children']['Insert'];

export function useChildren() {
  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchChildren = async () => {
    if (!user) {
      setChildren([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('children')
        .select('*')
        .eq('parent_id', user.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setChildren(data || []);
    } catch (error) {
      console.error('Error fetching children:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChildren();
  }, [user]);

  const addChild = async (childData: Omit<ChildInsert, 'parent_id'>) => {
    if (!user) return { error: new Error('User not authenticated') };

    try {
      // Check subscription limits before adding child
      const userSubscription = await SubscriptionService.getUserSubscription(user.id);
      const maxChildren = SubscriptionService.getMaxChildren(userSubscription);
      
      if (maxChildren === 0) {
        return { 
          data: null, 
          error: new Error('No active subscription. Please subscribe to add children.') 
        };
      }

      if (children.length >= maxChildren) {
        return { 
          data: null, 
          error: new Error(`You've reached the limit of ${maxChildren} child${maxChildren > 1 ? 'ren' : ''} for your current plan. Please upgrade to add more children.`) 
        };
      }

      const { data, error } = await supabase
        .from('children')
        .insert({
          ...childData,
          parent_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Initialize daily streak for new child
      await supabase
        .from('daily_streaks')
        .insert({
          child_id: data.id,
          current_streak: 0,
          longest_streak: 0,
          last_activity_date: new Date().toISOString().split('T')[0],
        });

      // Seed age-appropriate activities for the new child
      try {
        await seedActivitiesForChild(data);
      } catch (seedError) {
        console.warn('Failed to seed activities for child:', seedError);
        // Don't fail the child creation if activity seeding fails
      }

      setChildren(prev => [...prev, data]);
      return { data, error: null };
    } catch (error) {
      console.error('Error adding child:', error);
      return { data: null, error };
    }
  };

  const updateChild = async (id: string, updates: Partial<Child>) => {
    try {
      const { data, error } = await supabase
        .from('children')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setChildren(prev => prev.map(child => 
        child.id === id ? data : child
      ));

      return { data, error: null };
    } catch (error) {
      console.error('Error updating child:', error);
      return { data: null, error };
    }
  };

  return {
    children,
    loading,
    addChild,
    updateChild,
    refetch: fetchChildren,
  };
}